import { useState, useMemo } from 'react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input, SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { UserAvatar } from '@/components/ui/Avatar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useUsers, useRoles, useCreateUser, useUpdateUser, useDeleteUser, useHardDeleteUser, useChangePassword, useLastLogins } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { formatDate, formatDateTime } from '@/lib/utils'
import { MODULES, ALL_MODULE_IDS } from '@/lib/permissions'
import type { User, UserCreate, UserUpdate, Role } from '@/types'
import {
  Plus, Edit2, Trash2, Key, ChevronLeft, ChevronRight, X, Check, AlertTriangle, UserPlus, Shield, Loader2, UserCheck, Clock,
} from 'lucide-react'

const ROLE_COLORS: Record<string, 'default' | 'destructive' | 'secondary' | 'outline' | 'success' | 'warning' | 'info'> = {
  ceo: 'destructive',
  cfo: 'warning',
  sales_manager: 'info',
  sales_rep: 'default',
  marketing: 'success',
  viewer: 'secondary',
}

export function UsersPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [pwUser, setPwUser] = useState<User | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null)
  const [hardDeleteTarget, setHardDeleteTarget] = useState<User | null>(null)
  const [hardDeleteError, setHardDeleteError] = useState<string | null>(null)

  const currentUser = useAuthStore((s) => s.user)
  const roleLevel = currentUser?.role?.hierarchy_level ?? 0
  const lastLogins = useLastLogins()
  const hardDeleteMutation = useHardDeleteUser()

  const requestHardDelete = (user: User) => {
    setHardDeleteError(null)
    setHardDeleteTarget(user)
  }

  const confirmHardDelete = async () => {
    if (!hardDeleteTarget) return
    setHardDeleteError(null)
    try {
      await hardDeleteMutation.mutateAsync(hardDeleteTarget.id)
      setHardDeleteTarget(null)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      setHardDeleteError(axiosErr?.response?.data?.detail || 'Erro ao excluir usuário')
    }
  }

  const { data, isLoading } = useUsers({ page: 1, perPage: 100 })
  const { data: roles } = useRoles()

  const filteredUsers = useMemo(() => {
    let items = data?.items || []
    if (search) {
      const s = search.toLowerCase()
      items = items.filter(u => u.full_name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s))
    }
    if (filterRole) items = items.filter(u => u.role_id === filterRole)
    if (filterActive !== null) items = items.filter(u => u.is_active === filterActive)
    return items
  }, [data?.items, search, filterRole, filterActive])

  return (
    <div className="min-h-screen">
      

      <div className="p-6 space-y-4">

        {/* Last Login - Gestor Comercial+ */}
        {roleLevel >= 70 && (
          <Card>
            <CardContent className="pt-5 pb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                <UserCheck className="h-4 w-4 text-primary" />
                Ultimo Acesso dos Usuarios
              </h3>
              {lastLogins.isLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : lastLogins.isError ? (
                <p className="text-sm text-muted-foreground text-center py-3">Erro ao carregar dados</p>
              ) : (
                <DataGrid
                  gridId="last-logins"
                  columns={[
                    { id: 'name', header: 'Usuario', accessor: (u: any) => <span className="font-medium">{u.first_name} {u.last_name}</span>, sortValue: (u: any) => u.first_name + ' ' + u.last_name, filterValue: (u: any) => u.first_name + ' ' + u.last_name },
                    { id: 'email', header: 'Email', accessor: (u: any) => <span className="text-muted-foreground">{u.email}</span>, sortValue: (u: any) => u.email, filterValue: (u: any) => u.email },
                    { id: 'role', header: 'Perfil', accessor: (u: any) => <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{u.role_name}</span>, sortValue: (u: any) => u.role_name, filterValue: (u: any) => u.role_name },
                    { id: 'status', header: 'Status', accessor: (u: any) => u.is_active ? <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Ativo</span> : <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">Inativo</span>, sortValue: (u: any) => u.is_active ? 1 : 0, filterValue: (u: any) => u.is_active ? 'Ativo' : 'Inativo' },
                    { id: 'last_login', header: 'Ultimo Acesso', accessor: (u: any) => u.last_login_at ? <span className="text-sm">{formatDateTime(u.last_login_at)}</span> : <span className="text-xs text-amber-500 font-medium flex items-center gap-1"><Clock className="h-3 w-3" /> Nunca acessou</span>, sortValue: (u: any) => u.last_login_at ? new Date(u.last_login_at).getTime() : 0, filterValue: (u: any) => u.last_login_at ? formatDateTime(u.last_login_at) : 'Nunca acessou' },
                  ]}
                  data={lastLogins.data || []}
                  isLoading={lastLogins.isLoading}
                  emptyMessage="Nenhum dado de acesso"
                  pageSize={10}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 flex-wrap">
            <div className="w-64">
              <SearchInput placeholder="Buscar por nome ou email..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} onClear={() => setSearch('')} />
            </div>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setPage(1) }}>
              <option value="">Todos os perfis</option>
              {roles?.map((r) => <option key={r.id} value={r.id}>{r.display_name}</option>)}
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterActive === null ? '' : String(filterActive)} onChange={(e) => { setFilterActive(e.target.value === '' ? null : e.target.value === 'true'); setPage(1) }}>
              <option value="">Todos</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>
          <Button onClick={() => setShowCreate(true)} leftIcon={<UserPlus className="h-4 w-4" />}>
            Novo Usuario
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <DataGrid
              gridId="users"
              columns={[
                { id: 'name', header: 'Usuario', accessor: (user: any) => <div className="flex items-center gap-3"><UserAvatar name={user.full_name} size="sm" /><span className="font-medium">{user.full_name}</span></div>, sortValue: (u: any) => u.full_name, filterValue: (u: any) => u.full_name },
                { id: 'email', header: 'Email', accessor: (user: any) => <span className="text-muted-foreground">{user.email}</span>, sortValue: (u: any) => u.email, filterValue: (u: any) => u.email },
                { id: 'role', header: 'Perfil', accessor: (user: any) => <Badge variant={ROLE_COLORS[user.role?.name || ''] || 'secondary'}><Shield className="h-3 w-3 mr-1" />{user.role?.display_name || '-'}</Badge>, sortValue: (u: any) => u.role?.display_name || '', filterValue: (u: any) => u.role?.display_name || '' },
                { id: 'status', header: 'Status', accessor: (user: any) => user.is_active ? <Badge variant="success"><Check className="h-3 w-3 mr-1" />Ativo</Badge> : <Badge variant="destructive"><X className="h-3 w-3 mr-1" />Inativo</Badge>, sortValue: (u: any) => u.is_active ? 1 : 0, filterValue: (u: any) => u.is_active ? 'Ativo' : 'Inativo' },
                { id: 'created', header: 'Criado em', accessor: (user: any) => <span className="text-muted-foreground">{formatDate(user.created_at)}</span>, sortValue: (u: any) => new Date(u.created_at).getTime(), filterValue: (u: any) => formatDate(u.created_at) },
                { id: 'actions', header: 'Acoes', sortable: false, filterable: false, align: 'right' as const, accessor: (user: any) => <div className="flex justify-end gap-1" onClick={(e: any) => e.stopPropagation()}><Button variant="ghost" size="icon" onClick={() => setEditUser(user)} title="Editar"><Edit2 className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => setPwUser(user)} title="Alterar Senha"><Key className="h-4 w-4" /></Button>{user.is_active ? <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(user)} title="Desativar" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button> : <Button variant="ghost" size="icon" onClick={() => requestHardDelete(user)} title="Excluir permanentemente" disabled={hardDeleteMutation.isPending} className="bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground"><Trash2 className="h-4 w-4" /></Button>}</div> },
              ] as DataGridColumn<any>[]}
              data={filteredUsers}
              isLoading={isLoading}
              emptyMessage="Nenhum usuario encontrado"
              pageSize={20}
            />
          </CardContent>
        </Card>
      </div>

      {/* Create Modal */}
      {showCreate && <UserFormModal roles={roles || []} onClose={() => setShowCreate(false)} />}

      {/* Edit Modal */}
      {editUser && <UserFormModal user={editUser} roles={roles || []} onClose={() => setEditUser(null)} />}

      {/* Password Modal */}
      {pwUser && <PasswordModal user={pwUser} onClose={() => setPwUser(null)} />}

      {/* Delete Confirm */}
      {deleteConfirm && <DeleteConfirmModal user={deleteConfirm} onClose={() => setDeleteConfirm(null)} />}

      {/* Hard Delete Confirm */}
      <ConfirmDialog
        open={!!hardDeleteTarget}
        title={hardDeleteTarget ? `Excluir permanentemente "${hardDeleteTarget.full_name}"?` : 'Excluir permanentemente?'}
        message={hardDeleteError || 'O registro será removido do banco de dados. Esta ação NÃO pode ser desfeita.'}
        confirmLabel="Excluir definitivamente"
        cancelLabel="Cancelar"
        variant="destructive"
        isLoading={hardDeleteMutation.isPending}
        onConfirm={confirmHardDelete}
        onCancel={() => { setHardDeleteTarget(null); setHardDeleteError(null) }}
      />
    </div>
  )
}

// ---- Modals ----

function UserFormModal({ user, roles, onClose }: { user?: User; roles: Role[]; onClose: () => void }) {
  const isEdit = !!user
  const createMutation = useCreateUser()
  const updateMutation = useUpdateUser()

  const initialAllowed = (() => {
    const stored = (user?.attributes as { allowed_modules?: unknown } | undefined)?.allowed_modules
    if (Array.isArray(stored)) return stored.filter((s): s is string => typeof s === 'string')
    return ALL_MODULE_IDS
  })()
  const initialSkip2fa = Boolean((user?.attributes as { skip_2fa?: unknown } | undefined)?.skip_2fa)

  const [form, setForm] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    password: '',
    phone: user?.phone || '',
    role_id: user?.role_id || (roles[0]?.id || ''),
    is_active: user?.is_active ?? true,
    must_change_password: true,
    send_welcome_email: true,
  })
  const [allowedModules, setAllowedModules] = useState<string[]>(initialAllowed)
  const [skip2fa, setSkip2fa] = useState<boolean>(initialSkip2fa)
  const [error, setError] = useState('')

  const toggleModule = (id: string) => {
    setAllowedModules((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id])
  }
  const selectAllModules = () => setAllowedModules(ALL_MODULE_IDS)
  const clearAllModules = () => setAllowedModules([])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    // Celular/WhatsApp obrigatório só no cadastro novo. Em edição, deixe em branco
    // se admin não souber — o próprio usuário preenche depois (banner alerta).
    if (!isEdit && !form.phone.trim()) {
      setError('Celular/WhatsApp é obrigatório no cadastro')
      return
    }
    const existingAttrs = (user?.attributes as Record<string, unknown> | undefined) || {}
    const mergedAttributes = { ...existingAttrs, allowed_modules: allowedModules, skip_2fa: skip2fa }
    try {
      if (isEdit) {
        const { password: _pw, ...rest } = form
        const updateData = { ...rest, attributes: mergedAttributes }
        await updateMutation.mutateAsync({ userId: user!.id, data: updateData as UserUpdate })
      } else {
        const createData = { ...form, attributes: mergedAttributes }
        await createMutation.mutateAsync(createData as UserCreate)
      }
      onClose()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } }
      const detail = axiosErr?.response?.data?.detail
      if (typeof detail === 'string') {
        setError(detail)
      } else if (Array.isArray(detail) && detail.length > 0) {
        setError(detail.map((d: any) => d.msg || JSON.stringify(d)).join('; '))
      } else {
        setError('Erro ao salvar usuario')
      }
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Usuario' : 'Novo Usuario'}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Nome</label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div>
              <label className="text-sm font-medium">Sobrenome</label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div>
            <label className="text-sm font-medium">
              Celular/WhatsApp {!isEdit && <span className="text-destructive">*</span>}
            </label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={isEdit ? 'Opcional — o próprio usuário pode preencher' : '+55 11 99999-9999'}
              required={!isEdit}
            />
            {isEdit && !form.phone && (
              <p className="text-xs text-muted-foreground mt-1">Em branco — o usuário será alertado a preencher no próprio perfil.</p>
            )}
          </div>
          {!isEdit && (
            <div>
              <label className="text-sm font-medium">Senha</label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 chars, A-Z, a-z, 0-9" required />
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Perfil</label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.display_name} (Level {r.hierarchy_level})</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-input" />
              <label htmlFor="is_active" className="text-sm">Usuario ativo</label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="skip_2fa" checked={skip2fa} onChange={(e) => setSkip2fa(e.target.checked)} className="h-4 w-4 rounded border-input" />
              <label htmlFor="skip_2fa" className="text-sm">Dispensar 2FA <span className="text-muted-foreground">(não exigir setup nem código)</span></label>
            </div>
            {!isEdit && (
              <>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="must_change_password" checked={form.must_change_password} onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })} className="h-4 w-4 rounded border-input" />
                  <label htmlFor="must_change_password" className="text-sm">Trocar senha no primeiro acesso</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="send_welcome_email" checked={form.send_welcome_email} onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })} className="h-4 w-4 rounded border-input" />
                  <label htmlFor="send_welcome_email" className="text-sm">Enviar email com dados de acesso</label>
                </div>
              </>
            )}
          </div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Módulos acessíveis</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAllModules} className="text-primary hover:underline">Marcar todos</button>
                <span className="text-muted-foreground">·</span>
                <button type="button" onClick={clearAllModules} className="text-muted-foreground hover:underline">Limpar</button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Selecione quais áreas do sistema este usuário poderá acessar.</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
              {MODULES.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded px-1.5 py-0.5">
                  <input
                    type="checkbox"
                    checked={allowedModules.includes(m.id)}
                    onChange={() => toggleModule(m.id)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span>{m.label}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground pt-1">{allowedModules.length}/{MODULES.length} módulos selecionados</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" isLoading={isPending}>{isEdit ? 'Salvar' : 'Criar'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const mutation = useChangePassword()
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      await mutation.mutateAsync({ userId: user.id, data: { new_password: newPassword } })
      setSuccess(true)
      setTimeout(onClose, 1500)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      setError(axiosErr?.response?.data?.detail || 'Erro ao alterar senha')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Alterar Senha</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">Alterando senha de <strong>{user.full_name}</strong></p>
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          {success && <div className="p-3 rounded bg-green-500/10 text-green-600 text-sm">Senha alterada com sucesso!</div>}
          <div>
            <label className="text-sm font-medium">Nova Senha</label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 8 chars, A-Z, a-z, 0-9" required />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" isLoading={mutation.isPending}>Alterar</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DeleteConfirmModal({ user, onClose }: { user: User; onClose: () => void }) {
  const mutation = useDeleteUser()
  const [error, setError] = useState('')

  const handleDelete = async () => {
    try {
      await mutation.mutateAsync(user.id)
      onClose()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      setError(axiosErr?.response?.data?.detail || 'Erro ao desativar usuario')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Desativar Usuario</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Tem certeza que deseja desativar <strong>{user.full_name}</strong>?<br />
            O usuario nao conseguira acessar o sistema.
          </p>
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm mb-4">{error}</div>}
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={mutation.isPending}>Desativar</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
